import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import IconSearch from 'img/search-icon.svg';

import { IBatchRequestResponse } from 'models/Batch';
import { IUser, UserProfileTypes, UserType } from 'models/User';
import { IDomainSetting } from 'models/DomainSetting';
import UserService from 'services/user.service';
import DomainSettingService from 'services/domainSetting.service';

import AddEditEmployee from 'components/EmployeeManagerSlider';
import { Column, Row } from 'components/Layout/Grid';
import { Button } from 'components/Button';
import Form from 'components/Form';
import Table from 'components/Table';
import Text from 'components/Text';
import { UserFilter } from 'models/UserFilter';
import { modelEmployeeManager } from './model';

export const InternalUserTable: React.FC<any> = (props) => {

  const {
    currentTabId,
  } = props;

  const dispatch = useDispatch();
  const user = useSelector((state: { user: IUser; }) => state.user);
  const _domainSettingService = new DomainSettingService();

  const [users, setUsers] = useState<IBatchRequestResponse<IUser>>();
  const [componentAlreadyMount, setComponentAlreadyMount] = useState(false);

  const [tableOrderby, setTableOrderby] = useState('fullName asc');
  const [tableFilter, setTableFilter] = useState<UserFilter>({});

  const [filterInput, setFilterInput] = useState('');
  const [statusInput, setStatusInput] = useState<string | undefined>('0');
  const [profileInput, setProfileInput] = useState<keyof UserProfileTypes>(0);

  const [userProfileTypes, setUserProfileTypes] = useState(
    Array<IDomainSetting>()
  );

  const [currentPage, setCurrentPage] = useState(0);
  const pageSizeParam = 10;

  const processOrdination = () => {
    if (!tableOrderby) return undefined;

    try
    {
      const parts = tableOrderby.split(' ');
      const field = parts[0];
      const directionText = parts[1];
      const direction = directionText === 'desc' ? 1 : 0;

      if (field === 'enabled')
        return { field: 0, direction };
      if (field === 'fullName')
        return { field: 1, direction };

      return undefined;
    }
    catch(e) {
      console.error(e);
      return undefined;
    }
  };

  useEffect(() => {
    if (!currentTabId) return;
    dispatch({
      type: 'SET_CURRENT_TAB',
      data: currentTabId
    });
  }, []);

  const getUsers = useCallback(async () => {
    setUsers(undefined);

    const ordination = processOrdination();

    await new UserService()
      .filter(currentPage, pageSizeParam, tableFilter, ordination)
      .then((result: IBatchRequestResponse<IUser> | undefined) => {
        if (result) {
          const { data, totalPages, pageSize, total, error } = result;
          setUsers({ data, totalPages, pageSize, total, error });
        }
      });
  }, [tableFilter, tableOrderby, currentPage]);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'UserProfileTypes' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _userProfileTypes: Array<IDomainSetting> = result.data;

          setUserProfileTypes(_userProfileTypes);
        }
      });
  }, []);

  useEffect(() => {
    if (!componentAlreadyMount) {
      makeFilter();
      return setComponentAlreadyMount(true);
    }
    getUsers();
  }, [getUsers]);

  const makeFilterStatus = () => {
    if (statusInput === 'active')
      return false;

    return statusInput === 'disabled'
      ? true
      : undefined;
  };

  function makeFilter() {

    const status = makeFilterStatus();
    let data: UserFilter = {
      search: '',
      userType: UserType.Employee,
      profileType: undefined,
      deleted: status
    };

    if (filterInput !== '') {
      data.search = filterInput;
    }

    if (status !== undefined) {
      data.deleted = status;
    }

    if (
      profileInput !== undefined &&
      profileInput !== 0
    ) {
      data.profileType = profileInput;
    }

    setCurrentPage(0);
    setTableFilter(data);
  }

  const CustomerTable = useMemo(
    () => (
      <Table
        trackingKey="Grid Colaboradores"
        className="mt-3"
        setTableOrder={setTableOrderby}
        tableOrder={tableOrderby}
        pageSize={pageSizeParam}
        totalPages={users?.totalPages}
        onReload={getUsers}
        error={users?.error}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        data={users?.data}
        model={modelEmployeeManager(user, statusInput, getUsers)}
      />
    ),
    [users]
  );

  return (
    <>
      <Form id="table-search-employee-manager" onValidate={makeFilter}>
        <Row
          className="align-center mt-3 bg-white px-3 py-3 align-lg-none flex-lg-column"
          style={{ borderRadius: '10px 10px 0px 0px' }}
        >
          <Column>
            <Text size={12} className="mb-2 color-blue-gray mb-lg-2">
              Filtro
            </Text>
            <input
              onChange={(e) => setFilterInput(e.target.value)}
              type="email"
              className="w-lg-100"
              placeholder="Usuário ou e-mail"
              style={{ width: 390 }}
            />
          </Column>
          <Column className="ml-2 mt-lg-2 ml-lg-0">
            <Text size={12} className="mb-2 color-blue-gray mb-lg-2">
              Status
            </Text>
            <select
              name="status"
              defaultValue={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              className="w-lg-100"
              style={{ minWidth: 210 }}
            >
              <option value="0">Selecione</option>
              <option value="active">Ativo</option>
              <option value="disabled">Inativo</option>
            </select>
          </Column>
          <Column className="ml-2 mt-lg-2 ml-lg-0">
            <Text size={12} className="mb-2 color-blue-gray mb-lg-2">
              Perfil
            </Text>
            <Row className="align-center flex-sm-column">
              <select
                name="profile"
                defaultValue={typeof profileInput}
                onChange={(e) => setProfileInput(e.target.value as keyof UserProfileTypes)}
                className="w-lg-100 mb-sm-3"
                style={{ minWidth: 210 }}
              >
                <option value="">Selecione</option>
                {userProfileTypes.map((filter, index) => (
                  <option key={index} label={filter.value} value={filter.key} />
                ))}
              </select>
              <Row className="ml-2">
                <Button
                  tabIndex={0}
                  className="focus-button"
                  width="auto"
                  onClick={makeFilter}
                >
                  <img src={IconSearch} alt="" />
                </Button>
              </Row>
            </Row>
          </Column>
          <Row className="align-center mt-2 flex-1 justify-end flex-lg-column">
            <Button
              className="outline mt-2 ml-lg-0 w-lg-100"
              onClick={() =>
                dispatch({
                  type: 'OPEN_SLIDER',
                  data: {
                    title: 'Vincular funcionário',
                    subtitle:
                      'Preencha os campos abaixo para vincular um funcionário. O funcionário deverá usar o usuário de rede informado abaixo e sua senha de rede para acessar a plataforma.',
                    content: AddEditEmployee,
                    state: { getUsers },
                  },
                })
              }
              width="auto"
            >
              <Text className="color-primary font-bold px-1">
                Vincular Usuário
              </Text>
            </Button>
          </Row>
        </Row>
      </Form>
      {CustomerTable}
    </>
  );
};
